const binanceSockets: Record<string, WebSocket> = {};
const lastCloseMap: Record<string, number> = {};
let EPsCache: any[] = [];
let currentPriceType: string = 'last'; // 기본값은 'last'
let resetCacheCallback: any = null; // ✅ TradingView 캐시 초기화 콜백

export const CustomDatafeed = {
  init: (EPs: any[]) => {
    EPsCache = EPs || [];
  },

  setPriceType: (priceType: string) => {
    currentPriceType = priceType;
  },

  getPriceType: () => {
    return currentPriceType;
  },

  searchSymbols: (
    userInput: string,
    exchange: string,
    symbolType: string,
    onResultReadyCallback: (symbols: any[]) => void
  ) => {
    if (!EPsCache || EPsCache.length === 0) {
      console.warn('[searchSymbols] EPsCache is empty');
      onResultReadyCallback([]);
      return;
    }

    const filtered = EPsCache.filter((item) =>
      item.pairs.toLowerCase().includes(userInput.toLowerCase())
    ).map((item) => ({
      symbol: item.pairs,
      full_name: item.pairs,
      description: item.name || item.pairs,
      exchange: 'FossX',
      ticker: item.pairs,
      type: 'crypto',
    }));

    onResultReadyCallback(filtered);
  },

  onReady: (cb) => {
    setTimeout(
      () =>
        cb({
          supported_resolutions: [
            '1',
            '3',
            '5',
            '15',
            '30',
            '1h',
            '2h',
            '4h',
            '6h',
            '8h',
            '12h',
            '1d',
            '3d',
            '1w',
            '1M',
          ],
        }),
      0
    );
  },

  resolveSymbol: (symbolName, onSymbolResolvedCallback) => {
    const pricescaleMap: Record<string, number> = {
      'BTC/USDT': 10,
      '1000PEPE/USDT': 10000000,
      'XRP/USDT': 10000,
      'BCH/USDT': 100,
      'ADA/USDT': 10000,
      'AVAX/USDT': 1000,
      'DOGE/USDT': 100000,
      'DOT/USDT': 1000,
      'UNI/USDT': 1000,
      'LINK/USDT': 1000,
      'TRX/USDT': 100000,
      'SUI/USDT': 10000,
    };

    const normalized =
      symbolName === 'PEPE/USDT' ? '1000PEPE/USDT' : symbolName;
    const pricescale = pricescaleMap[normalized] || 100;

    onSymbolResolvedCallback({
      name: normalized,
      ticker: normalized,
      type: 'crypto',
      session: '24x7',
      timezone: 'Asia/Seoul',
      exchange: 'FossX',
      minmov: 1,
      pricescale,
      has_intraday: true,
      supported_resolutions: [
        '1',
        '3',
        '5',
        '15',
        '30',
        '1h',
        '2h',
        '4h',
        '6h',
        '8h',
        '12h',
        '1d',
        '3d',
        '1w',
        '1M',
      ],
      volume_precision: 2,
      data_status: 'streaming',
    });
  },

  // priceType에 따른 히스토리 데이터 URL 생성
  getHistoryUrl: (
    symbol: string,
    apiResolution: string,
    cursor: number,
    end: number,
    chunkSize: number,
    useByNova: boolean
  ) => {
    const baseUrl = useByNova
      ? 'https://nudf.bynova.net'
      : 'https://fapi.binance.com';

    if (useByNova) {
      // ByNova URL - priceType에 따른 엔드포인트 변경
      const endpoint =
        currentPriceType === 'last'
          ? 'history'
          : currentPriceType === 'mark'
            ? 'markprice-history'
            : 'indexprice-history';
      return `${baseUrl}/${endpoint}?symbol=${symbol}&resolution=${apiResolution}&from=${Math.floor(cursor / 1000)}&to=${Math.floor(end / 1000)}`;
    } else {
      // Binance URL - priceType에 따른 엔드포인트 변경
      const endpoint =
        currentPriceType === 'last'
          ? 'klines'
          : currentPriceType === 'mark'
            ? 'markPriceKlines'
            : 'indexPriceKlines';
      return `${baseUrl}/fapi/v1/${endpoint}?${currentPriceType === 'index' ? 'pair' : 'symbol'}=${symbol}&interval=${apiResolution}&startTime=${cursor}&endTime=${end}&limit=${chunkSize}`;
    }
  },

  // priceType에 따른 WebSocket URL 생성
  getWebSocketUrl: (symbol: string, interval: string) => {
    if (currentPriceType === 'last') {
      // Last price - kline 스트림
      return `wss://fstream.binance.com/ws/${symbol.toLowerCase()}@kline_${interval}`;
    } else if (currentPriceType === 'mark' || currentPriceType === 'index') {
      // Mark price - markPrice 스트림 (1초마다 업데이트)
      return `wss://fstream.binance.com/ws/${symbol.toLowerCase()}@markPrice@1s`;
    }

    // 기본값
    return `wss://fstream.binance.com/ws/${symbol.toLowerCase()}@kline_${interval}`;
  },

  async getBars(
    symbolInfo,
    resolution,
    periodParams,
    onHistoryCallback,
    onErrorCallback
  ) {
    try {
      const symbol = symbolInfo.name.replace('/', '').toUpperCase();
      const apiResolution = normalizeResolution(resolution);

      const resolutionToMs = {
        '1m': 60_000,
        '3m': 180_000,
        '5m': 300_000,
        '15m': 900_000,
        '30m': 1_800_000,
        '1h': 3_600_000,
        '2h': 7_200_000,
        '4h': 14_400_000,
        '6h': 21_600_000,
        '8h': 28_800_000,
        '12h': 43_200_000,
        '1d': 86_400_000,
        '3d': 259_200_000,
        '1w': 604_800_000,
        '1M': 2_592_000_000,
      };

      const chunkSizeMap = {
        '1m': 500,
        '3m': 500,
        '5m': 500,
        '15m': 400,
        '30m': 400,
        '1h': 300,
        '2h': 300,
        '4h': 250,
        '6h': 200,
        '8h': 200,
        '12h': 150,
        '1d': 120,
        '3d': 100,
        '1w': 80,
        '1M': 50,
      };

      const barMs = resolutionToMs[apiResolution];
      const chunkSize = chunkSizeMap[apiResolution] || 300;
      const to = periodParams.to * 1000;
      const from = Math.max(0, periodParams.from * 1000);
      const useByNova =
        symbol.includes('1000PEPE') || symbol.toLowerCase().includes('xmr');

      const allBars: any[] = [];
      for (let cursor = from; cursor < to; cursor += barMs * chunkSize) {
        const end = Math.min(cursor + barMs * chunkSize, to);
        const url = CustomDatafeed.getHistoryUrl(
          symbol,
          apiResolution,
          cursor,
          end,
          chunkSize,
          useByNova
        );

        const res = await fetch(url);
        const data = await res.json();

        const bars = useByNova
          ? data.t.map((t: number, i: number) => {
              let barTime: number;

              // ✅ 일봉 시간 정규화: Daily bars need KST conversion (+9 hours)
              if (resolution === '1D' || resolution === 'D' || resolution === '1d') {
                const adjustedTimestamp = (t / 1000 + 9 * 3600) * 1000;
                barTime = Math.floor(adjustedTimestamp / 86400000) * 86400000;
              } else {
                barTime = t;
              }

              return {
                time: barTime,
                open: data.o[i],
                high: data.h[i],
                low: data.l[i],
                close: data.c[i],
                volume: data.v[i] || 0,
              };
            })
          : data.map((d: any) => {
              let barTime: number;

              // ✅ 일봉 시간 정규화: Daily bars need KST conversion (+9 hours)
              if (resolution === '1D' || resolution === 'D' || resolution === '1d') {
                const adjustedTimestamp = (d[0] / 1000 + 9 * 3600) * 1000;
                barTime = Math.floor(adjustedTimestamp / 86400000) * 86400000;
              } else {
                barTime = d[0];
              }

              return {
                time: barTime,
                open: +d[1],
                high: +d[2],
                low: +d[3],
                close: +d[4],
                volume: +d[5],
              };
            });

        allBars.push(...bars);
      }

      const uniqueSortedBars = Array.from(
        new Map(allBars.map((b) => [b.time, b])).values()
      )
        .sort((a, b) => a.time - b.time)
        .filter((bar) => bar.time >= from && bar.time <= to);

      onHistoryCallback(uniqueSortedBars, {
        noData: uniqueSortedBars.length === 0,
      });
    } catch (err) {
      console.error('getBars error', err);
      onErrorCallback(err);
    }
  },

  subscribeBars(symbolInfo, resolution, onRealtimeCallback, subscribeUID, onResetCacheNeededCallback?) {
    // ✅ resetCacheCallback 저장
    if (onResetCacheNeededCallback) {
      resetCacheCallback = onResetCacheNeededCallback;

      // ✅ 전역 함수로 노출
      (window as any).__chartResetCache = () => {
        if (resetCacheCallback) {
          console.log('[CustomDatafeed] 캐시 초기화 호출');
          resetCacheCallback();
        }
      };
    }
    // const symbol = symbolInfo.name.replace('/', '').toUpperCase();
    // const interval = normalizeResolution(resolution);
    // const wsUrl = `wss://fstream.binance.com/ws/${symbol.toLowerCase()}@kline_${interval}`;
    //
    // if (binanceSockets[subscribeUID]) return;
    //
    // const ws = new WebSocket(wsUrl);
    //
    // ws.onmessage = (event) => {
    //   requestAnimationFrame(() => {
    //     try {
    //       const { k } = JSON.parse(event.data);
    //       const last = lastCloseMap[symbol];
    //
    //       if (last !== undefined && last === parseFloat(k.c)) return;
    //       lastCloseMap[symbol] = parseFloat(k.c);
    //
    //       onRealtimeCallback({
    //         time: k.t, open: +k.o, high: +k.h,
    //         low: +k.l, close: +k.c, volume: +k.v
    //       });
    //     } catch (err) {
    //       console.warn("Realtime data parse error:", err);
    //     }
    //   });
    // };
    //
    // ws.onerror = (e) => console.warn("WebSocket error:", e);
    // ws.onclose = () => console.log(`[WS Closed] ${symbol}@${interval}`);
    //
    // binanceSockets[subscribeUID] = ws;

    const symbol = symbolInfo.name.replace('/', '').toUpperCase();
    const interval = normalizeResolution(resolution);
    const wsUrl = CustomDatafeed.getWebSocketUrl(symbol, interval);

    if (binanceSockets[subscribeUID]) return;

    const ws = new WebSocket(wsUrl);
    binanceSockets[subscribeUID] = ws;

    let lastUpdate = 0;
    const MIN_UPDATE_INTERVAL = 100;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log(`[WS RESUME] ${symbol}@${interval}`);
        lastUpdate = 0;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    ws.onmessage = (event) => {
      const now = Date.now();
      if (now - lastUpdate < MIN_UPDATE_INTERVAL) return;
      lastUpdate = now;

      try {
        const data = JSON.parse(event.data);

        if (currentPriceType === 'last') {
          // Last price (kline) 데이터 처리
          const { k } = data;
          if (!k) {
            console.warn('[WebSocket] kline 데이터가 없음:', data);
            return;
          }

          const last = lastCloseMap[symbol];
          const close = parseFloat(k.c);
          if (last !== undefined && last === close) return;

          lastCloseMap[symbol] = close;

          onRealtimeCallback({
            time: k.t,
            open: +k.o,
            high: +k.h,
            low: +k.l,
            close: +k.c,
            volume: +k.v,
          });
        } else if (
          currentPriceType === 'mark' ||
          currentPriceType === 'index'
        ) {
          // Mark price 또는 Index price 데이터 처리
          const priceField = currentPriceType === 'mark' ? 'p' : 'i'; // mark: 'p', index: 'i'
          const price = parseFloat(data[priceField]);

          if (!price || isNaN(price)) {
            console.warn(
              `[WebSocket] ${currentPriceType} price 데이터가 유효하지 않음:`,
              data
            );
            return;
          }

          const last = lastCloseMap[symbol];
          if (last !== undefined && last === price) return;

          lastCloseMap[symbol] = price;

          // Mark/Index price는 단일 가격 데이터이므로 OHLC를 모두 같은 값으로 설정
          onRealtimeCallback({
            time: data.E || data.T || Date.now(), // 이벤트 시간 또는 타임스탬프
            open: price,
            high: price,
            low: price,
            close: price,
            volume: 0, // Mark/Index price는 볼륨이 없음
          });
        }
      } catch (e) {
        console.warn('[WebSocket] parse error:', e);
        console.warn('[WebSocket] 원본 데이터:', event.data);
      }
    };

    ws.onerror = (err) => {
      console.warn('[WebSocket] error:', err);
    };

    ws.onclose = () => {
      console.log(`[WS Closed] ${symbol}@${interval}`);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      delete binanceSockets[subscribeUID];
      delete lastCloseMap[symbol];
    };
  },

  unsubscribeBars(subscribeUID) {
    const ws = binanceSockets[subscribeUID];
    if (ws) {
      try {
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        ws.close();
      } catch (e) {
        console.warn('WebSocket cleanup error:', e);
      }
      delete binanceSockets[subscribeUID];
    }
  },
};

function normalizeResolution(res: string): string {
  const map: Record<string, string> = {
    '1': '1m',
    '3': '3m',
    '5': '5m',
    '15': '15m',
    '30': '30m',
    '60': '1h',
    '120': '2h',
    '240': '4h',
    '360': '6h',
    '480': '8h',
    '720': '12h',
    '1D': '1d',
    '3D': '3d',
    '1W': '1w',
    '1M': '1M',
  };
  return map[res] || res;
}
